import { Injectable, inject } from '@angular/core';
import { FormName } from '@core/data-access/models/form/form.model';
import { FormMediator } from '@core/data-access/services/form/form-mediator.service';
import { FormTicketLimitValidity } from '@core/data-access/services/form/form-ticket-limit-validity';
import { ExhibitorActions } from '@core/data-access/store/exhibitor/exhibitor.actions';
import { SharedActions } from '@core/data-access/store/shared/shared.actions';
import {
  SavedTicketHolderActions,
  SavedTicketHolderFormActions
} from '@core/features/account/features/saved-ticket-holders/store/saved-ticket-holder.actions';
import { BuyerInformationActions } from '@core/features/buyer-information/store/buyer-information.actions';
import { BuyerQuestionnaireActions } from '@core/features/buyer-questionnaire/store/buyer-questionnaire.actions';
import { TicketAssignmentActions } from '@core/features/ticket-assignment/store/ticket-assignment.actions';
import { Store, select } from '@ngrx/store';
import { BookingSmartPromotionService } from '@products/services/booking-smart-promotion.service';
import { ContingentBookingService } from '@products/services/contingent-booking.service';
import { ProductService } from '@products/services/product.service';
import { consoleLog } from '@shared/app-utils';
import { ErrorHandlingService } from '@shared/error-handling/error-handling.service';
import { FormsService } from '@shared/forms/forms.service';
import { WINDOW } from '@shared/tokens';
import { ResetReducer as AdditionalServicesResetReducer } from '@store/additional-services/additional-services.actions';
import {
  PartialResetReducer as CustomizationPartialResetReducer,
  ResetShoppingStartTime
} from '@store/customization/customization.actions';
import { CustomizationService } from '@store/customization/customization.service';
import { PartialResetReducer as ExhibitionPartialResetReducer } from '@store/exhibition/exhibition.actions';
import { ResetReducer as LegitimationResetReducer } from '@store/legitimation/legitimation.actions';
import { getAllBookedTariffs } from '@store/products/booking/product/product-booking.selectors';
import { ResetProductsActions } from '@store/products/product.actions';
import {
  ResetReducer as StepsFormResetReducer,
  ResetReducer as StepsFormsResetReducer
} from '@store/step-forms/steps-forms.actions';
import { ResetReducer as UserResetReducer } from '@store/user/user.actions';
import { isNil } from 'lodash';
import { Subject, first, fromEvent, iif, map, merge, of, share } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class AppService {
  private readonly store = inject(Store);
  private readonly window = inject(WINDOW);
  private readonly customizationService = inject(CustomizationService);
  private readonly errorHandlingService = inject(ErrorHandlingService);
  private readonly formsService = inject(FormsService);
  private readonly contingentBookingService = inject(ContingentBookingService);
  private readonly productService = inject(ProductService);
  private readonly bookingSmartPromotionService = inject(BookingSmartPromotionService);
  private readonly formMediator = inject(FormMediator);
  private readonly formTicketLimitValidity = inject(FormTicketLimitValidity);

  readonly #_isRendered = new Subject<boolean>();

  readonly isRendered$ = this.#_isRendered.asObservable();

  readonly isOnline$ = this.setIsOnline$();

  markAsRendered() {
    this.#_isRendered.next(true);
    this.#_isRendered.complete();
  }

  resetReducersWithCallback(releaseAllVouchersAndTickets: boolean, callback: () => void) {
    if (releaseAllVouchersAndTickets) {
      this.productService.releaseAllVouchersAndProductBookings();
    }

    const sharedObservable$ = this.store.pipe(select(getAllBookedTariffs)).pipe(first(), share());

    sharedObservable$.subscribe(() => {
      callback();
    });

    return sharedObservable$;
  }

  resetReducers(releaseAllVouchersAndTickets = true, releaseContingents = true, releasePromoCodes = true) {
    consoleLog('Reset reducers');
    //clear all previously received errors from the API (so we don't interpret errors that occurred in a previous shopping process):
    this.errorHandlingService.clearAllErrors();
    this.formsService.resetInputsValidity();

    if (releaseContingents) {
      this.contingentBookingService
        .releaseAllContingentReservations$()
        .subscribe((orderUuid) => consoleLog(`Releasing contingent tickets for uuid: ${orderUuid}`));
    }

    if (releasePromoCodes) {
      this.bookingSmartPromotionService
        .releasePromoCodeDiscountReservation$()
        .subscribe((uuid) => consoleLog(`Release promo code discount reservation for uuid: ${uuid}`));
    }

    this.formMediator.resetFormStates([FormName.SavedTicketHolders]);
    this.formTicketLimitValidity.resetIsTicketLimitPerEmailAddressValidationProcessing();

    //we have to make this operation synchronous as otherwise setStyles method would potentially be called before the store state has been changed
    //(e.g. the customization template wouldn't be changed):
    return this.resetReducersWithCallback(releaseAllVouchersAndTickets, () => {
      new Promise<void>((resolve) => {
        this.store.dispatch(new CustomizationPartialResetReducer()); // customization reducer keeps localized images and operator settings
        this.store.dispatch(SavedTicketHolderFormActions.resetSavedTicketHoldersUnsavedFormFields());
        this.store.dispatch(ExhibitorActions.resetReducer());
        this.store.dispatch(BuyerInformationActions.resetReducer());
        this.store.dispatch(BuyerQuestionnaireActions.resetReducer());
        this.store.dispatch(TicketAssignmentActions.resetReducer());
        this.store.dispatch(SharedActions.resetReducer());
        this.store.dispatch(new ExhibitionPartialResetReducer());
        this.store.dispatch(new AdditionalServicesResetReducer());
        this.store.dispatch(new LegitimationResetReducer());
        this.store.dispatch(new StepsFormsResetReducer());
        this.store.dispatch(ResetProductsActions.resetProductSelectionReducer());
        this.store.dispatch(ResetProductsActions.resetBookingReducer());
        this.store.dispatch(ResetProductsActions.resetWorkshopReducer());
        this.store.dispatch(ResetProductsActions.resetHolderReducer());
        this.store.dispatch(ResetProductsActions.resetStatusReducer());

        resolve();
      }).then(() => {
        this.customizationService.setStyles();
      });
    });
  }

  resetReducersWithUser(releaseAllVouchersAndTickets = true, releaseContingents = true, releasePromoCodes = true) {
    consoleLog('Reset event including user');
    //clear all previously received errors from the API (so we don't interpret errors that occurred in a previous shopping process):
    this.errorHandlingService.clearAllErrors();
    this.formsService.resetInputsValidity();

    if (releaseContingents) {
      this.contingentBookingService
        .releaseAllContingentReservations$()
        .subscribe((orderUuid) => consoleLog(`Releasing contingent tickets for uuid: ${orderUuid}`));
    }

    if (releasePromoCodes) {
      this.bookingSmartPromotionService
        .releasePromoCodeDiscountReservation$()
        .subscribe((uuid) => consoleLog(`Release promo code discount reservation for uuid: ${uuid}`));
    }

    this.formMediator.resetFormStates([]);
    this.formTicketLimitValidity.resetIsTicketLimitPerEmailAddressValidationProcessing();

    // customization reducer is kept on logout
    return this.resetReducersWithCallback(releaseAllVouchersAndTickets, () => {
      new Promise<void>((resolve) => {
        this.store.dispatch(new ResetShoppingStartTime());
        this.store.dispatch(new UserResetReducer());
        this.store.dispatch(SavedTicketHolderActions.resetReducer());
        this.store.dispatch(ExhibitorActions.resetReducer());
        this.store.dispatch(BuyerInformationActions.resetReducer());
        this.store.dispatch(BuyerQuestionnaireActions.resetReducer());
        this.store.dispatch(TicketAssignmentActions.resetReducer());
        this.store.dispatch(SharedActions.resetReducer());
        this.store.dispatch(new ExhibitionPartialResetReducer());
        this.store.dispatch(new StepsFormResetReducer());
        this.store.dispatch(new AdditionalServicesResetReducer());
        this.store.dispatch(new LegitimationResetReducer());
        this.store.dispatch(new StepsFormsResetReducer());
        this.store.dispatch(ResetProductsActions.resetProductSelectionReducer());
        this.store.dispatch(ResetProductsActions.resetBookingReducer());
        this.store.dispatch(ResetProductsActions.resetWorkshopReducer());
        this.store.dispatch(ResetProductsActions.resetHolderReducer());
        this.store.dispatch(ResetProductsActions.resetStatusReducer());

        resolve();
      }).then(() => {
        this.customizationService.setStyles();
      });
    });
  }

  private setIsOnline$() {
    return iif(
      () => !isNil(this.window?.navigator),
      merge(
        of(navigator.onLine),
        fromEvent(window, 'online').pipe(map(() => true)),
        fromEvent(window, 'offline').pipe(map(() => false))
      ),
      of(true)
    );
  }
}
